import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle,
  Search,
  Download,
  Trash2,
  RefreshCw,
  FileText,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Copy,
} from 'lucide-react';
import api from '../services/api';
import { formatTimestamp, getLevelColor, truncateMessage } from '../utils/logUtils';

function ErrorLogViewer() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Version indicator to verify fixes are deployed
  // console.log('🕐 ErrorLogViewer v2.0 - Melbourne timezone enabled');

  // Filters
  const [filters, setFilters] = useState({
    level: 'all',
    date: 'today',
    search: '',
    limit: 100,
    offset: 0,
  });

  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    hasMore: false,
  });

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    error: 0,
    warn: 0,
    info: 0,
    debug: 0,
  });

  // Selected log for details
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params = new URLSearchParams({
        level: filters.level,
        date: filters.date,
        search: filters.search,
        limit: filters.limit.toString(),
        offset: filters.offset.toString(),
      });

      const response = await api.get(`/config/error-logs?${params}`);
      setLogs(response.logs);
      setPagination(response.pagination);
      setStats(response.stats);
    } catch (err) {
      console.error('Error loading logs:', err);
      setError('Failed to load error logs');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset pagination when filters change
    }));
  };

  const handleSearch = e => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      offset: 0,
    }));
  };

  const handlePageChange = newOffset => {
    setFilters(prev => ({
      ...prev,
      offset: newOffset,
    }));
  };

  const handleExport = async (format = 'json') => {
    try {
      const params = new URLSearchParams({
        format,
        level: filters.level,
        date: filters.date,
      });

      const response = await fetch(`/api/config/error-logs/export?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('swarm_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `error-logs-${filters.date}-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSuccess(`Logs exported successfully as ${format.toUpperCase()}`);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export logs');
    }
  };

  const handleDeleteLogs = async () => {
    try {
      await api.delete('/config/error-logs', {
        data: {
          confirm: 'DELETE_LOGS',
          date: filters.date,
        },
      });

      setSuccess('Logs deleted successfully');
      setShowDeleteConfirm(false);
      loadLogs(); // Reload logs
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete logs');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-900">Log Viewer</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => loadLogs()}
            disabled={loading}
            className="btn btn-secondary btn-small flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Errors</p>
              <p className="text-2xl font-bold text-red-600">{stats.error}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Warnings</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.warn}</p>
            </div>
            <AlertTriangle className="w-8 h-8 text-yellow-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Info</p>
              <p className="text-2xl font-bold text-blue-600">{stats.info}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Debug</p>
              <p className="text-2xl font-bold text-gray-600">{stats.debug}</p>
            </div>
            <FileText className="w-8 h-8 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Date Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              value={filters.date}
              onChange={e => handleFilterChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="all">All Time</option>
            </select>
          </div>

          {/* Level Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
            <select
              value={filters.level}
              onChange={e => handleFilterChange('level', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Levels</option>
              <option value="error">Errors Only</option>
              <option value="warn">Warnings Only</option>
              <option value="info">Info Only</option>
              <option value="debug">Debug Only</option>
            </select>
          </div>

          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <form onSubmit={handleSearch} className="flex">
              <input
                type="text"
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                placeholder="Search messages..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                type="submit"
                className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
              >
                <Search className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Actions */}
          <div className="flex space-x-1">
            <button
              onClick={() => handleExport('json')}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center space-x-1"
              title="Export as JSON"
            >
              <Download className="w-3 h-3" />
              <span className="hidden sm:inline">JSON</span>
            </button>
            <button
              onClick={() => handleExport('csv')}
              className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded flex items-center space-x-1"
              title="Export as CSV"
            >
              <Download className="w-3 h-3" />
              <span className="hidden sm:inline">CSV</span>
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded flex items-center space-x-1"
              title="Clear Logs"
            >
              <Trash2 className="w-3 h-3" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <FileText className="w-5 h-5 text-green-500" />
          <span className="text-green-700">{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto">
            <X className="w-4 h-4 text-green-500" />
          </button>
        </div>
      )}

      {/* Logs Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                  Timestamp
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Level
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                  Source
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  Trace
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-3 py-8 text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
                      <span className="text-gray-600">Loading logs...</span>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-3 py-8 text-center text-gray-500">
                    No logs found matching your criteria
                  </td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="truncate" title={formatTimestamp(log.timestamp)}>
                        {formatTimestamp(log.timestamp)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getLevelColor(log.level)}`}
                      >
                        {log.level?.toUpperCase() || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-900">
                      <div className="truncate" title={log.message}>
                        {truncateMessage(log.message)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="truncate" title={log.source}>
                        {log.source}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        {log.stack && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Stack
                          </span>
                        )}
                        {log.requestId && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Req
                          </span>
                        )}
                        {!log.stack && !log.requestId && (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-purple-600 hover:text-purple-900 flex items-center space-x-1"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                        <span className="hidden sm:inline">View</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(Math.max(0, filters.offset - filters.limit))}
                disabled={filters.offset === 0}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(filters.offset + filters.limit)}
                disabled={!pagination.hasMore}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{filters.offset + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(filters.offset + filters.limit, pagination.total)}
                  </span>{' '}
                  of <span className="font-medium">{pagination.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(Math.max(0, filters.offset - filters.limit))}
                    disabled={filters.offset === 0}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handlePageChange(filters.offset + filters.limit)}
                    disabled={!pagination.hasMore}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Log Entry Details</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const fullDetails = {
                      timestamp: selectedLog.timestamp,
                      level: selectedLog.level,
                      message: selectedLog.message,
                      source: selectedLog.source,
                      id: selectedLog.id,
                      stack: selectedLog.stack,
                      requestId: selectedLog.requestId,
                      method: selectedLog.method,
                      url: selectedLog.url,
                      ip: selectedLog.ip,
                      userAgent: selectedLog.userAgent,
                      duration: selectedLog.duration,
                      meta: selectedLog.meta,
                    };
                    navigator.clipboard.writeText(JSON.stringify(fullDetails, null, 2));
                    setSuccess('Full log details copied to clipboard');
                  }}
                  className="text-xs text-purple-600 hover:text-purple-800 flex items-center space-x-1"
                >
                  <Copy className="w-3 h-3" />
                  <span>Copy All</span>
                </button>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Timestamp</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {formatTimestamp(selectedLog.timestamp)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Level</label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getLevelColor(selectedLog.level)}`}
                    >
                      {selectedLog.level?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Source</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedLog.source}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ID</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedLog.id}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Message</label>
                  <div className="mt-1 p-3 bg-gray-50 rounded-md">
                    <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                      {selectedLog.message}
                    </pre>
                  </div>
                </div>

                {selectedLog.stack && (
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-gray-700">Stack Trace</label>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(selectedLog.stack);
                          setSuccess('Stack trace copied to clipboard');
                        }}
                        className="text-xs text-purple-600 hover:text-purple-800 flex items-center space-x-1"
                      >
                        <Copy className="w-3 h-3" />
                        <span>Copy</span>
                      </button>
                    </div>
                    <div className="mt-1 p-3 bg-red-50 rounded-md">
                      <pre className="text-sm text-red-800 whitespace-pre-wrap font-mono">
                        {selectedLog.stack}
                      </pre>
                    </div>
                  </div>
                )}

                {/* Enhanced Error Context */}
                {selectedLog.level === 'error' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Error Context</label>
                    <div className="mt-1 p-3 bg-red-50 rounded-md">
                      <div className="space-y-2">
                        {selectedLog.requestId && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-red-600">Request ID:</span>
                            <code className="text-xs bg-red-100 px-2 py-1 rounded">
                              {selectedLog.requestId}
                            </code>
                          </div>
                        )}
                        {selectedLog.method && selectedLog.url && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-red-600">HTTP Request:</span>
                            <code className="text-xs bg-red-100 px-2 py-1 rounded">
                              {selectedLog.method} {selectedLog.url}
                            </code>
                          </div>
                        )}
                        {selectedLog.ip && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-red-600">IP Address:</span>
                            <code className="text-xs bg-red-100 px-2 py-1 rounded">
                              {selectedLog.ip}
                            </code>
                          </div>
                        )}
                        {selectedLog.userAgent && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-red-600">User Agent:</span>
                            <code className="text-xs bg-red-100 px-2 py-1 rounded truncate max-w-md">
                              {selectedLog.userAgent}
                            </code>
                          </div>
                        )}
                        {selectedLog.duration && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-red-600">Duration:</span>
                            <code className="text-xs bg-red-100 px-2 py-1 rounded">
                              {selectedLog.duration}
                            </code>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {selectedLog.meta && Object.keys(selectedLog.meta).length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Metadata</label>
                    <div className="mt-1 p-3 bg-gray-50 rounded-md">
                      <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.meta, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">Delete Logs</h3>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to delete all logs for "{filters.date}"? This action cannot be
                undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="btn btn-secondary btn-small"
                >
                  Cancel
                </button>
                <button onClick={handleDeleteLogs} className="btn btn-danger btn-small">
                  Delete Logs
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorLogViewer;
